import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  AlertCircle,
  ArrowLeft,
  ClipboardCheck,
  FlaskConical,
  Lock,
  Stethoscope,
  Upload,
  User as UserIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { FunctionalTestUploadDialog } from "@/features/functional_tests/FunctionalTestUploadDialog";
import { LabOrderDialog } from "@/features/laboratory/LabOrderDialog";
import { SpecialtyExtraFields } from "@/features/examinations/SpecialtyExtraFields";
import { ApiError } from "@/lib/api/client";
import {
  type DoctorSpecialty,
  getExamination,
  getSection,
  type SectionUpdatePayload,
  type SectionVerdict,
  upsertSection,
} from "@/lib/api/examinations";
import { getPatient } from "@/lib/api/patients";
import { searchMkb10 } from "@/lib/api/references";
import { useAuthStore } from "@/stores/auth";

interface FormValues {
  complaints: string;
  objective_findings: string;
  diagnosis_code: string;
  diagnosis_text: string;
  recommendations: string;
  verdict: SectionVerdict | null;
  extra_fields: Record<string, unknown>;
}

export function SectionEditorPage() {
  const { exam_id, specialty } = useParams<{
    exam_id: string;
    specialty: DoctorSpecialty;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mkbQuery, setMkbQuery] = useState("");
  const [debouncedMkbQuery] = useDebouncedValue(mkbQuery, 250);
  const [selectedMkbLabel, setSelectedMkbLabel] = useState<string>("");
  const [labDialogOpen, labDialog] = useDisclosure(false);
  const [funcDialogOpen, funcDialog] = useDisclosure(false);
  const canOrderLab = useAuthStore((s) => s.hasPermission("lab_test.order"));
  const canUploadFunctional = useAuthStore((s) => s.hasPermission("functional_test.upload"));

  const sectionQuery = useQuery({
    queryKey: ["section", exam_id, specialty],
    queryFn: () => getSection(exam_id!, specialty!),
    enabled: !!exam_id && !!specialty,
    retry: false,
  });

  const examQuery = useQuery({
    queryKey: ["examination", exam_id],
    queryFn: () => getExamination(exam_id!),
    enabled: !!exam_id,
  });

  const patientQuery = useQuery({
    queryKey: ["patient", examQuery.data?.patient_id],
    queryFn: () => getPatient(examQuery.data!.patient_id),
    enabled: !!examQuery.data?.patient_id,
  });

  const mkbQueryResult = useQuery({
    queryKey: ["mkb10", debouncedMkbQuery],
    queryFn: () => searchMkb10(debouncedMkbQuery),
  });

  const form = useForm<FormValues>({
    initialValues: {
      complaints: "",
      objective_findings: "",
      diagnosis_code: "",
      diagnosis_text: "",
      recommendations: "",
      verdict: null,
      extra_fields: {},
    },
  });

  const section = sectionQuery.data;

  useEffect(() => {
    if (section) {
      form.setValues({
        complaints: section.complaints ?? "",
        objective_findings: section.objective_findings ?? "",
        diagnosis_code: section.diagnosis_code ?? "",
        diagnosis_text: section.diagnosis_text ?? "",
        recommendations: section.recommendations ?? "",
        verdict: section.verdict ?? null,
        extra_fields: (section.extra_fields as Record<string, unknown>) ?? {},
      });
      // Pre-fill the search query with the saved diagnosis code so the API
      // returns its full label for the input — this prevents the input from
      // showing just the bare code until the user types something.
      if (section.diagnosis_code) {
        setMkbQuery(section.diagnosis_code);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id]);

  // When the API returns the currently-selected MKB code with its full label,
  // capture that label so the dropdown input always renders it (and our
  // mkbData fallback uses it instead of the bare code).
  useEffect(() => {
    const selectedCode = form.values.diagnosis_code;
    if (!selectedCode) return;
    const fromApi = (mkbQueryResult.data?.items ?? []).find(
      (m) => m.code === selectedCode,
    );
    if (fromApi) {
      const label = `${fromApi.code} — ${fromApi.description_uz ?? fromApi.description_ru}`;
      if (label !== selectedMkbLabel) {
        setSelectedMkbLabel(label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mkbQueryResult.data, form.values.diagnosis_code]);

  const mutation = useMutation({
    mutationFn: (payload: SectionUpdatePayload) =>
      upsertSection(exam_id!, specialty!, payload),
    onSuccess: () => {
      notifications.show({ color: "blue", message: t("sections.saved") });
      queryClient.invalidateQueries({ queryKey: ["section", exam_id, specialty] });
      queryClient.invalidateQueries({ queryKey: ["examination", exam_id] });
      queryClient.invalidateQueries({ queryKey: ["worklist"] });
    },
  });

  // All hooks (incl. useMemo) MUST be declared before any early return.
  const mkbData = useMemo(() => {
    const items = (mkbQueryResult.data?.items ?? []).map((m) => ({
      value: m.code,
      label: `${m.code} — ${m.description_uz ?? m.description_ru}`,
    }));
    const selectedCode = form.values.diagnosis_code;
    if (selectedCode) {
      const idx = items.findIndex((i) => i.value === selectedCode);
      if (idx >= 0) {
        if (selectedMkbLabel && items[idx].label !== selectedMkbLabel) {
          items[idx] = { value: selectedCode, label: selectedMkbLabel };
        }
      } else {
        items.unshift({
          value: selectedCode,
          label: selectedMkbLabel || selectedCode,
        });
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mkbQueryResult.data, form.values.diagnosis_code, selectedMkbLabel]);

  if (sectionQuery.isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  if (sectionQuery.error || !section) {
    return (
      <Stack gap="md" align="flex-start">
        <UnstyledButton
          onClick={() => navigate(-1)}
          style={{ color: "var(--mantine-color-gray-6)", padding: 8, borderRadius: 999 }}
        >
          <ArrowLeft size={22} strokeWidth={2} />
        </UnstyledButton>
        <Alert color="red" icon={<AlertCircle size={16} />} radius="lg" w="100%">
          {sectionQuery.error instanceof ApiError
            ? sectionQuery.error.message
            : t("sections.not_found")}
        </Alert>
      </Stack>
    );
  }

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  const handleSubmit = (values: FormValues) => {
    const payload: SectionUpdatePayload = {
      complaints: values.complaints || undefined,
      objective_findings: values.objective_findings || undefined,
      diagnosis_code: values.diagnosis_code || undefined,
      diagnosis_text: values.diagnosis_text || undefined,
      recommendations: values.recommendations || undefined,
      verdict: values.verdict ?? undefined,
      extra_fields:
        Object.keys(values.extra_fields ?? {}).length > 0
          ? values.extra_fields
          : undefined,
    };
    mutation.mutate(payload);
  };

  const isUnhealthy = form.values.verdict === "unhealthy";
  const patient = patientQuery.data;
  // mkbData is computed at the top of the component (before any early return)
  // to keep the hook order stable across renders.
  const isSearchingActively =
    mkbQuery.length > 0 && mkbQuery !== selectedMkbLabel;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="md">
          <UnstyledButton
            onClick={() => navigate(-1)}
            style={{ color: "var(--mantine-color-gray-6)", padding: 8, borderRadius: 999 }}
          >
            <ArrowLeft size={22} strokeWidth={2} />
          </UnstyledButton>
          <Stack gap={2}>
            <Title order={3} fw={700}>
              {t(`users.specialties.${specialty}`)}
            </Title>
            <Group gap={6}>
              <Badge color={section.completed_at ? "brand" : "gray"} variant="light" radius="sm">
                {section.completed_at
                  ? t("examinations.statuses.completed")
                  : t("examinations.statuses.pending")}
              </Badge>
              {section.locked && (
                <Badge color="red" variant="light" radius="sm" leftSection={<Lock size={12} />}>
                  {t("sections.locked")}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
        <Group gap="md">
          {canOrderLab && (
            <Button
              variant="light"
              radius="xl"
              leftSection={<FlaskConical size={16} strokeWidth={2.2} />}
              onClick={labDialog.open}
            >
              {t("laboratory.order_button")}
            </Button>
          )}
          {canUploadFunctional && (
            <Button
              variant="light"
              radius="xl"
              leftSection={<Upload size={16} strokeWidth={2.2} />}
              onClick={funcDialog.open}
            >
              {t("functional_tests.upload_button")}
            </Button>
          )}
        </Group>
      </Group>

      {patient && (
        <Card padding="lg" radius="xl">
          <Group gap="md" wrap="nowrap">
            <Avatar
              size={48}
              radius="md"
              variant="gradient"
              gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
            >
              <UserIcon size={20} strokeWidth={2} color="white" />
            </Avatar>
            <Stack gap={2}>
              <Group gap="sm">
                <Text fw={700} size="md" component={Link} to={`/patients/${patient.id}`}>
                  {patient.family_name} {patient.given_name}
                </Text>
                <Badge variant="light" color="gray" radius="sm" size="sm">
                  {patient.card_number}
                </Badge>
              </Group>
              <Text c="dimmed" size="xs">
                {patient.birth_date} · {t("patients.years_old", { age: patient.age })}
              </Text>
            </Stack>
          </Group>
        </Card>
      )}

      {errorMessage && (
        <Alert color="red" icon={<AlertCircle size={16} />} radius="lg">
          {errorMessage}
        </Alert>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {specialty !== "laboratory" && (
            <Card padding="xl" radius="xl">
              <Group gap="sm" mb="lg" align="center">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <Stethoscope size={18} strokeWidth={2} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("sections.section.examination")}
                </Title>
              </Group>
              <Stack gap="md">
                <Textarea
                  label={t("sections.fields.complaints")}
                  placeholder={t("placeholder.complaints")}
                  minRows={3}
                  autosize
                  disabled={section.locked}
                  {...form.getInputProps("complaints")}
                />
                <Textarea
                  label={t("sections.fields.objective_findings")}
                  placeholder={t("placeholder.objective_findings")}
                  minRows={3}
                  autosize
                  disabled={section.locked}
                  {...form.getInputProps("objective_findings")}
                />
              </Stack>
            </Card>
          )}

          {specialty !== "laboratory" && (
            <Card padding="xl" radius="xl">
              <Group gap="sm" mb="lg" align="center">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <ClipboardCheck size={18} strokeWidth={2} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("sections.section.diagnosis")}
                </Title>
              </Group>
              <Stack gap="md">
                <Select
                  label={t("sections.fields.diagnosis_code")}
                  placeholder={t("placeholder.type_to_search")}
                  searchable
                  clearable
                  data={mkbData}
                  searchValue={mkbQuery}
                  onSearchChange={setMkbQuery}
                  nothingFoundMessage={
                    isSearchingActively ? t("sections.no_mkb_match") : null
                  }
                  disabled={section.locked}
                  value={form.values.diagnosis_code || null}
                  onChange={(v) => {
                    form.setFieldValue("diagnosis_code", v ?? "");
                    if (v) {
                      const label = mkbData.find((d) => d.value === v)?.label;
                      if (label) setSelectedMkbLabel(label);
                    } else {
                      setSelectedMkbLabel("");
                      setMkbQuery("");
                    }
                  }}
                />
                <Textarea
                  label={t("sections.fields.diagnosis_text")}
                  placeholder={t("placeholder.diagnosis_text")}
                  required={isUnhealthy}
                  minRows={2}
                  autosize
                  disabled={section.locked}
                  {...form.getInputProps("diagnosis_text")}
                />
                <Textarea
                  label={t("sections.fields.recommendations")}
                  placeholder={t("placeholder.recommendations")}
                  minRows={2}
                  autosize
                  disabled={section.locked}
                  {...form.getInputProps("recommendations")}
                />
              </Stack>
            </Card>
          )}

          {specialty && (
            <SpecialtyExtraFields
              specialty={specialty}
              values={form.values.extra_fields}
              onChange={(next) => form.setFieldValue("extra_fields", next)}
              disabled={section.locked}
              patientSex={patient?.sex ?? null}
            />
          )}

          <Card padding="xl" radius="xl">
            <Group gap="sm" mb="lg" align="center">
              <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                <ClipboardCheck size={18} strokeWidth={2} />
              </ThemeIcon>
              <Title order={5} fw={700}>
                {t("sections.fields.verdict")}
              </Title>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <VerdictButton
                label={t("sections.verdicts.healthy")}
                selected={form.values.verdict === "healthy"}
                color="brand"
                disabled={section.locked}
                onClick={() => form.setFieldValue("verdict", "healthy")}
              />
              <VerdictButton
                label={t("sections.verdicts.unhealthy")}
                selected={form.values.verdict === "unhealthy"}
                color="red"
                disabled={section.locked}
                onClick={() => form.setFieldValue("verdict", "unhealthy")}
              />
            </SimpleGrid>
          </Card>

          <Group justify="flex-end" gap="md">
            <UnstyledButton
              onClick={() => navigate(-1)}
              style={{ color: "var(--mantine-color-gray-6)", fontWeight: 500, fontSize: 14 }}
            >
              {t("common.cancel")}
            </UnstyledButton>
            <Button
              type="submit"
              radius="xl"
              size="md"
              loading={mutation.isPending}
              disabled={section.locked}
              color="brand.7"
              px={32}
              style={{ boxShadow: "0 6px 16px rgba(37, 99, 235, 0.25)" }}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </form>

      {exam_id && (
        <>
          <LabOrderDialog
            examId={exam_id}
            opened={labDialogOpen}
            onClose={labDialog.close}
          />
          <FunctionalTestUploadDialog
            examId={exam_id}
            opened={funcDialogOpen}
            onClose={funcDialog.close}
          />
        </>
      )}
    </Stack>
  );
}

function VerdictButton({
  label,
  selected,
  onClick,
  color,
  disabled,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  color: string;
  disabled?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 12,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        border: selected
          ? `1.5px solid var(--mantine-color-${color}-6)`
          : "1px solid var(--mantine-color-gray-2)",
        background: selected
          ? `var(--mantine-color-${color}-0)`
          : "var(--mantine-color-gray-0)",
        color: selected
          ? `var(--mantine-color-${color}-7)`
          : "var(--mantine-color-gray-7)",
        fontWeight: selected ? 700 : 500,
        fontSize: 15,
        transition: "all 120ms ease",
        boxShadow: selected
          ? `0 0 0 4px var(--mantine-color-${color}-1)`
          : "none",
      }}
    >
      {label}
    </UnstyledButton>
  );
}
